import React, { useState, useEffect, useCallback } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { SkeletonRow } from "@/components/ui/Skeleton";
import { UserReviewType } from "@/types/db/user_reviews";
import { userService } from "@/services/userService";
import usePermissions from "@/hooks/usePermissions";
import useToast from "@/hooks/useToast";
import { ReviewCard } from "@/components/screens/user/ReviewCard";

export interface RelatedReviewsScreenProps {
  externalAddTrigger?: number;
}

export function RelatedReviewsScreen({ externalAddTrigger }: RelatedReviewsScreenProps) {
  const { colors } = useTheme();
  const currentEntity = useCurrentEntity();
  const { user_reviews: reviewPermissions } = usePermissions();
  const toast = useToast();

  const [reviews, setReviews] = useState<UserReviewType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const userId = currentEntity?.currentId;
  const isUserPanel = currentEntity?.currentPanel === "user";

  // Fetch reviews
  const fetchReviews = useCallback(async () => {
    if (!userId || !isUserPanel) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const fetchedReviews = await userService.getReviewsByUserId(userId);
      setReviews(fetchedReviews);
    } catch (err: any) {
      console.error("Failed to fetch related reviews:", err);
      setError(err.message || "Failed to load reviews");
    } finally {
      setLoading(false);
    }
  }, [userId, isUserPanel]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  // Handle external add trigger from header button
  useEffect(() => {
    if (externalAddTrigger && externalAddTrigger > 0 && reviewPermissions.create && userId) {
      handleAddReview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalAddTrigger, reviewPermissions.create, userId]);

  const handleAddReview = useCallback(() => {
    const newReview: UserReviewType = {
      id: `new-${Date.now()}`,
      tenant_id: "",
      user_id: userId || "",
      rating: 5,
      message: "",
      created_by: "",
      created_at: new Date().toISOString(),
      meta_data: { category: "", period: "" },
    };
    setReviews((prev) => [newReview, ...prev]);
  }, [userId]);

  const handleSaveReview = useCallback(
    async (review: UserReviewType) => {
      if (!userId) return;

      try {
        const isNew = review.id.startsWith("new-");

        if (isNew) {
          const created = await userService.createReview({
            user_id: userId,
            rating: review.rating,
            message: review.message,
            submitted_by: review.submitted_by,
            meta_data: review.meta_data,
          });
          setReviews((prev) => prev.map((r) => (r.id === review.id ? created : r)));
          toast.showToast({ title: "Success", message: "Review created", toastType: "success" });
        } else {
          const updated = await userService.updateReview(review.id, {
            rating: review.rating,
            message: review.message,
            submitted_by: review.submitted_by,
            meta_data: review.meta_data,
          });
          setReviews((prev) => prev.map((r) => (r.id === review.id ? updated : r)));
          toast.showToast({ title: "Success", message: "Review updated", toastType: "success" });
        }
      } catch (err: any) {
        toast.showToast({ title: "Error", message: err.message, toastType: "error" });
      }
    },
    [userId, toast],
  );

  const handleRemoveReview = useCallback(
    async (reviewId: string) => {
      const isNew = reviewId.startsWith("new-");

      if (isNew) {
        // Just remove from local state
        setReviews((prev) => prev.filter((r) => r.id !== reviewId));
        return;
      }

      try {
        await userService.deleteReview(reviewId);
        setReviews((prev) => prev.filter((r) => r.id !== reviewId));
        toast.showToast({ title: "Success", message: "Review deleted", toastType: "success" });
      } catch (err: any) {
        toast.showToast({ title: "Error", message: err.message, toastType: "error" });
      }
    },
    [toast],
  );

  const getAverageRating = () => {
    const savedReviews = reviews.filter((r) => !r.id.startsWith("new-"));
    if (savedReviews.length === 0) return 0;
    const sum = savedReviews.reduce((acc, r) => acc + r.rating, 0);
    return (sum / savedReviews.length).toFixed(1);
  };

  // If no read permission, show access denied
  if (!reviewPermissions.read) {
    return (
      <View style={styles.container}>
        <View style={styles.centered}>
          <MaterialCommunityIcons name="lock" size={48} color={colors.textSecondary} />
          <ThemedText type="defaultSemiBold" style={{ marginTop: 16 }}>
            Access Denied
          </ThemedText>
          <ThemedText style={{ opacity: 0.7, marginTop: 8 }}>You don&apos;t have permission to view reviews</ThemedText>
        </View>
      </View>
    );
  }

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <View style={styles.centered}>
          <MaterialCommunityIcons name="alert-circle-outline" size={48} color={colors.dangerText} />
          <ThemedText style={{ marginTop: 12, opacity: 0.7 }}>{error}</ThemedText>
        </View>
      </View>
    );
  }

  const savedReviewsCount = reviews.filter((r) => !r.id.startsWith("new-")).length;

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        {/* Summary Card */}
        {savedReviewsCount > 0 && (
          <View style={[styles.summaryCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <View style={styles.summaryContent}>
              <View style={styles.averageRating}>
                <ThemedText type="title" style={{ fontSize: 32 }}>
                  {getAverageRating()}
                </ThemedText>
                <MaterialCommunityIcons name="star" size={24} color={colors.amber.text} style={{ marginLeft: 4 }} />
              </View>
              <ThemedText style={{ opacity: 0.6, marginTop: 4 }}>
                Based on {savedReviewsCount} review{savedReviewsCount !== 1 ? "s" : ""}
              </ThemedText>
            </View>
          </View>
        )}

        {/* Reviews List */}
        {reviews.length > 0 ? (
          reviews.map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              canUpdate={reviewPermissions.update}
              canDelete={reviewPermissions.delete}
              onSave={handleSaveReview}
              onRemove={handleRemoveReview}
            />
          ))
        ) : (
          <View style={styles.centered}>
            <MaterialCommunityIcons name="star-outline" size={48} color={colors.textSecondary} />
            <ThemedText style={{ marginTop: 12, opacity: 0.7 }}>No reviews found</ThemedText>
            {reviewPermissions.create && (
              <ThemedText style={{ opacity: 0.5, marginTop: 4, fontSize: 12 }}>Use the + button to add one</ThemedText>
            )}
          </View>
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    gap: 12,
  },
  loadingContainer: {
    padding: 16,
    gap: 12,
  },
  centered: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  summaryCard: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 12,
    padding: 16,
  },
  summaryContent: {
    alignItems: "center",
  },
  averageRating: {
    flexDirection: "row",
    alignItems: "center",
  },
});
